import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { child, ref, set, serverTimestamp, orderByChild, query, increment } from '@firebase/database';
import { auth, database } from '../api/firebase';
import { IMessage, IGifMessage, ITextMessage } from '../types/room';
import { DataSnapshot, onValue } from 'firebase/database';
import useRoomcode from '../hooks/useRoomcode';
import useNickname from '../hooks/useNickname';
import AnimatedIcon from './AnimatedIcon';
import GifSearch from './GifSearch';
import GifMessage from './GifMessage';
import { MAX_TEXT_MESSAGE_LENGTH } from '../constants/form';
import TextMessage from './TextMessage';

// const EMOJIS: string[] = ['❤️', '🔥', '💦'];

interface ChatProps {
  shouldDisplay: boolean;
}

const Chat: React.FC<ChatProps> = ({ shouldDisplay }) => {
  const [message, setMessage] = useState<string>('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);

  const [showGifSearch, setShowGifSearch] = useState<boolean>(false);
  const [emojiPositions, setEmojiPositions] = useState<any[]>([]);
  const reactButtonRef = useRef<HTMLButtonElement>(null);

  const roomcode = useRoomcode();
  const nickname = useNickname(auth.currentUser!.uid, roomcode);

  useEffect(
    () => {
      const messagesRef = query(ref(database, `bubbles/${roomcode}/messages`), orderByChild('timestamp'));
      const unsubscribe = onValue(messagesRef, (snapshot: DataSnapshot) => {
        if (snapshot.exists()) {
          const messagesArray: IMessage[] = [];
          snapshot.forEach(messageSnapshot => {
            messagesArray.push(messageSnapshot.val());
          });
          setMessages(messagesArray);
        } else {
          setMessages([]);
        }
        scrollToCurrentMessage();
      });
      return unsubscribe;
    },
    [roomcode]
  );

  useEffect(
    () => {
      const numReactsRef = ref(database, `bubbles/${roomcode}/numReacts`);
      const unsubscribe = onValue(numReactsRef, () => {
        const position = { left: jitter(reactButtonRef.current!.offsetLeft), top: reactButtonRef.current!.offsetTop };
        setEmojiPositions(prevState => [...prevState, position]);
      });
      return unsubscribe;
    },
    [roomcode]
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const messageTrimmed: string = message.trim();
    if (!messageTrimmed) return;
    sendTextMessage(messageTrimmed);
    setMessage('');
  };

  const sendGifMessage = async (gifId: string) => {
    const message: IGifMessage = {
      gifId,
      author: nickname,
      timestamp: serverTimestamp(),
      user_id: auth.currentUser!.uid,
      message_id: uuidv4()
    };
    await sendMessage(message);
    scrollToCurrentMessage(400);
  }

  const sendTextMessage = async (messageText: string) => {
    const message: ITextMessage = {
      author: nickname,
      text: messageText,
      timestamp: serverTimestamp(),
      user_id: auth.currentUser!.uid,
      message_id: uuidv4()
    };
    await sendMessage(message);
    scrollToCurrentMessage();
  };

  const sendMessage = async (message: IMessage) => {
    const newMessageRef = child(ref(database), `bubbles/${roomcode}/messages/${message.message_id}`);
    await set(newMessageRef, message);
  }

  const scrollToCurrentMessage = (timeout: number = 0): void => {
    setTimeout(() => {
      anchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, timeout);
  };

  const handleReactButtonClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    incrementNumReacts();
  }

  const incrementNumReacts = async () => {
    const numReactsRef = ref(database, `bubbles/${roomcode}/numReacts`);
    await set(numReactsRef, increment(1));
  }

  const jitter = (num: number) => {
    return Math.floor(Math.random() * num);
  }

  const getRandomEmoji = (): string => {
    return '❤️';
    // return EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
  }

  return (
    <div className={`max-h-screen-7/10 flex flex-grow flex-col justify-end ${shouldDisplay ? '' : 'hidden'} mb-2 sm:mt-auto sm:flex`}>
      <div className="overflow-y-auto overflow-x-hidden mb-2">
        {messages.map((message: IMessage, idx: number, array: IMessage[]) => {
          const previousMessage = array[idx - 1];
          const nextMessage = array[idx + 1];
          // refactor this shit
          if ('gifId' in message) {
            const gifMessage = message as IGifMessage;
            return (
              <GifMessage
                gifId={gifMessage.gifId}
                author={gifMessage.author}
                key={gifMessage.message_id}
              />);
          }
          const { text, author, user_id, message_id } = message as ITextMessage;
          return (
            <TextMessage
              text={text}
              author={author}
              user_id={user_id}
              key={message_id}
              prevUserId={previousMessage?.user_id}
              nextUserId={nextMessage?.user_id}
            />);
        })}
        <div ref={anchorRef} />
      </div>
      {showGifSearch &&
        <GifSearch
          sendGif={sendGifMessage}
          hideGifSearch={() => setShowGifSearch(false)}
        />
      }
      <form onSubmit={handleSubmit} className='flex bg-black bg-opacity-30'>
        <input
          type="text"
          maxLength={MAX_TEXT_MESSAGE_LENGTH}
          value={message}
          placeholder="write something"
          className="text-white bg-black bg-opacity-0 border-none w-full p-1 placeholder-gray-400 focus:outline-none"
          onChange={(e) => setMessage(e.target.value)}
        />
        {emojiPositions.map((position, i) => <AnimatedIcon icon={getRandomEmoji()} left={position.left} top={position.top} key={i} />)}
        {/* hack: the first button serves as the submit button, so that the others dont submit the button */}
        <button className='hidden' />
        <button
          className='text-base m-1.5 px-2 py-1 outline-none'
          ref={reactButtonRef}
          onClick={handleReactButtonClick}
        >
          ❤️
        </button>
      </form>
    </div>
  );
}

export default Chat;
